import { useCallback, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { useApi } from '@paradigm/api/src';
import { listAccounts, AccountResponse } from '@paradigm/api/src/accounts';
import {
  ripeLemon,
  paradigmLightBlue,
} from '@paradigm/design-system/src/colors';
import { Resp } from '@paradigm/api/src/fetch-api';

import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import Select from '@paradigm/design-system/src/components/Select';
import { useTrackAction } from '@paradigm/features/src/analytics';
import { Maybe } from '@paradigm/utils/types';
import DefaultAccountsModalV2 from '#/components/user-preferences/components/default-accounts/DefaultAccountsModal';
import CustomAccountOption from './components/CustomAccountOption';
import DefaultAccountIndicator from './components/DefaultAccountIndicator';
import Label from './components/Label';
import Warning from './components/Warning';
import CustomValueContainer from './components/CustomValueContainer';

import AccountSelectionProvider from './AccountSelectionProvider';
import EditDefaultButton from './EditDefaultButton';

import type { AccountOption } from './types';

interface Props {
  value: AccountOption['value'] | null | undefined;
  venue: string;
  productCode?: Maybe<string>;
  variant?: 'vertical' | 'horizontal';
  onChange: (account: AccountOption['value']) => void;
  onAccountsFetched?: (resp: Resp<AccountResponse[]>) => void;
  cachedAccounts?: AccountResponse[];
}

export default function AccountSelect(props: Props) {
  const {
    value,
    venue,
    variant,
    onChange,
    onAccountsFetched,
    cachedAccounts,
    productCode,
  } = props;
  const [{ resp }, fetchAccounts] = useApi(listAccounts);

  useEffect(() => {
    if (!cachedAccounts) fetchAccounts({ venue }, onAccountsFetched);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue, cachedAccounts]);

  const options = useMemo(
    () =>
      resp?.ok === true
        ? resp.data.map((acc) => ({
            label: acc.name,
            value: acc.name,
            id: acc.id,
            isDefault: acc.is_default,
          }))
        : cachedAccounts
        ? cachedAccounts.map((acc) => ({
            label: acc.name,
            value: acc.name,
            id: acc.id,
            isDefault: acc.is_default,
          }))
        : undefined,
    [resp, cachedAccounts],
  );

  const currentOption =
    options?.find((option) => option.value === value) ??
    options?.find((option) => option.isDefault) ??
    null;

  const error = resp?.ok === false ? 'Failed to load accounts' : null;

  useEffect(() => {
    if (currentOption != null && currentOption.value !== value)
      onChange(currentOption.value);
    // In order to only propagate the initial value calculated
    // internally, only the options can be watched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  return (
    <AccountSelection
      venue={venue}
      productCode={productCode}
      options={options}
      value={currentOption}
      variant={variant}
      error={error}
      onChange={(option) => onChange(option?.value ?? '')}
    />
  );
}

interface AccountSelectionProps {
  options: readonly AccountOption[] | undefined;
  productCode?: Maybe<string>;
  value: AccountOption | null | undefined;
  venue: string;
  variant?: 'vertical' | 'horizontal';
  error?: string | null;
  onChange: (account: Maybe<AccountOption>) => void;
}

export function AccountSelection(props: AccountSelectionProps) {
  const {
    options,
    value,
    venue,
    variant = 'vertical',
    error = null,
    productCode,
    onChange,
  } = props;
  const trackAction = useTrackAction();

  const handleChange = useCallback(
    (option: AccountOption | null) => {
      trackAction('update account', { isDefault: false });
      if (option != null) onChange(option);
    },
    [onChange, trackAction],
  );

  const handleDefaultAccountChange = useCallback(
    (option: AccountOption | null) => {
      trackAction('update account', { isDefault: true });
      onChange(option);
    },
    [onChange, trackAction],
  );

  const onChangeDefault = useCallback(
    (accountName: string | null) => {
      const option = options?.find((o) => o.value === accountName);
      handleDefaultAccountChange(option ?? null);
    },
    [handleDefaultAccountChange, options],
  );

  const placeholder =
    error != null
      ? 'No accounts available'
      : options == null
      ? 'Loading accounts'
      : options.length > 0
      ? 'Select Account'
      : 'No accounts available';

  const isDefault = value?.isDefault ?? false;
  const hasDefaultOption = options?.some((option) => option.isDefault) === true;

  const isDisabled = options == null;
  const displayNoDefaultWarning =
    options != null && !hasDefaultOption && value == null;

  const hasWarning =
    (options != null && value == null) ||
    displayNoDefaultWarning ||
    error != null;

  const warningLabel =
    error != null ? (
      <Warning>{error}</Warning>
    ) : displayNoDefaultWarning ? (
      <Warning>No default account selected</Warning>
    ) : null;

  const selectCss = hasWarning
    ? WARNING_CSS
    : options != null && !isDefault
    ? NON_DEFAULT_CSS
    : '';

  const selectComponents = {
    Option: CustomAccountOption,
    ...(isDefault
      ? {
          ValueContainer: CustomValueContainer,
          IndicatorsContainer: DefaultAccountIndicator,
        }
      : undefined),
  };

  const selectField = (
    <div data-testid="select-container">
      <Select
        isBordered
        id={FIELD_ID}
        options={options}
        value={value}
        placeholder={placeholder}
        onChange={handleChange}
        isLoading={error == null && options == null}
        isDisabled={isDisabled}
        components={selectComponents}
        css={selectCss}
      />
    </div>
  );

  return (
    <AccountSelectionProvider>
      <DefaultAccountsModalV2
        onChangeDefault={onChangeDefault}
        accountSelectInfo={{ venue, productCode }}
      />

      {variant === 'vertical' ? (
        <Col as={AccountSelectContainer}>
          <Row>
            <Label htmlFor={FIELD_ID}>Account</Label>
            {!isDisabled && <EditDefaultButton venue={venue} />}
          </Row>
          <div>
            {selectField}
            {warningLabel}
          </div>
        </Col>
      ) : (
        <Row as={AccountSelectContainer}>
          <Col css="line-height: 18px; margin-right: 20px;">
            <Label htmlFor={FIELD_ID}>Account</Label>
            <EditDefaultButton venue={venue} css="font-size: 0.75rem;" />
          </Col>
          <div css="flex: 1;">
            {selectField}
            {warningLabel}
          </div>
        </Row>
      )}
    </AccountSelectionProvider>
  );
}

const FIELD_ID = 'select-account';

const WARNING_CSS = css`
  border: 2px solid ${ripeLemon};
`;

const NON_DEFAULT_CSS = css`
  border: 2px solid ${paradigmLightBlue};
`;

export const AccountSelectContainer = styled.div.attrs({
  'data-testid': 'select-account-container',
  className: 'account-select-container',
})`
  margin-bottom: 1rem;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    > div {
      padding-bottom: 6px;

      label {
        font-size: 1rem;
      }
      button {
        font-size: 0.875rem;
      }
    }
    .select-control {
      height: 40px;
      font-size: 1rem;
    }
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
`;
